// Pricing.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Classes from './Pricing.module.css';
import SparklesIcon, { TickCircleIcon } from '../../../../../utils/Icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import backend from '../../../../../api';

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const cardIn = {
  hidden: { opacity: 0, y: 28, scale: 0.98, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: 'blur(0)',
    transition: { duration: 0.55, ease: 'easeOut' }
  }
};

// util
const money = (n) => `$${(+n).toFixed(0)}`;

// normalize any legacy keys coming from backend
const normalizePlanKey = (key) => {
  if (!key) return 'free';
  switch (key) {
    case 'basic':
      return 'individual';
    case 'premium':
      return 'studio';
    default:
      return key; // 'free', 'individual', 'studio', etc.
  }
};

// treat these statuses as "has an active sub" for disabling UI
const ACTIVE_STATUSES = ['active', 'trialing', 'past_due', 'unpaid', 'incomplete'];

export default function Pricing() {
  const [billing, setBilling] = useState('monthly'); // 'monthly' | 'yearly'
  const isYearly = billing === 'yearly';
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);

  const plans = [
    {
      key: 'individual',
      title: 'Individual',
      subtitle: 'Best for freelancers',
      monthly: 29,
      cta: 'Get started',
      features: [
        'Unlimited Downloads',
        'Saved Collections',
        'Advanced Filters',
        'Manufacturer Visibility'
      ]
    },
    {
      key: 'studio',
      title: 'Studio',
      subtitle: 'Best for large teams & studios',
      monthly: 49,
      highlight: true,
      cta: 'Get started',
      features: [
        'Shared Project Libraries',
        'Team Seat Management',
        'Priority Support',
        'Early Access to New Features'
      ]
    }
  ];

  // Derive current plan from user
  const rawPlanKey =
    user?.activeSubscription?.plan?.key ||
    user?.subscription ||
    'free';

  const currentPlanKey = normalizePlanKey(rawPlanKey);

  const subscriptionStatus =
    user?.activeSubscription?.status ||
    (currentPlanKey !== 'free' ? 'active' : 'none');

  const hasActiveSubscription = ACTIVE_STATUSES.includes(subscriptionStatus);

  // 15% off when yearly (display only)
  const priceLabel = (p) => {
    const perMonth = isYearly ? p.monthly * 0.85 : p.monthly;
    return (
      <>
        <span className={Classes.price}>{money(perMonth)}</span>
        <span className={Classes.per}>/month</span>
      </>
    );
  };

  const billedNote = () => (isYearly ? 'Billed Annually · Save 15%' : 'Billed Monthly');

  const handleGetStarted = async (plan) => {
    // If not logged in, send to signup and remember plan selection in state if you want
    if (!user || !user._id) {
      navigate('/signup/customer', {
        state: {
          fromPricing: true,
          planKey: plan.key,
          billingCycle: billing,
        },
      });
      return;
    }

    try {
      const body = {
        planKey: plan.key,              // 'individual' | 'studio'
        billingCycle: billing,          // 'monthly' | 'yearly'
        userId: user._id,
        companyId: user.companyId || null,
        type: 'subscription',
      };

      const res = await backend.post('/api/stripe/create-checkout-session', body);
      const url = res?.data?.url;
      if (!url) {
        alert('Could not start checkout. Please try again.');
        return;
      }
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err) {
      console.error('checkout error:', err?.response?.data || err.message);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Something went wrong while starting checkout.';
      alert(msg);
    }
  };

  return (
    <motion.div
      id="pricing"
      className={`${Classes.hero} layoutPadding`}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.25 }}
    >
      {/* Badge */}
      <div className='badge'>
        <SparklesIcon size={17} color='var(--secondary-color)' />
        Pricing
      </div>

      {/* Heading + sub */}
      <h2 className='homeTitle'>Join LightTrack Premium</h2>
      <p className='homeSubTitle'>
        Powerful lighting data management for architecture, design, and 3D workflows
      </p>

      {/* Billing Toggle */}
      <motion.div className={Classes.toggleWrap} variants={fadeUp}>
        <div className={Classes.billingToggle}>
          <motion.div
            className={Classes.toggleBg}
            animate={{ left: billing === 'monthly' ? '4px' : 'calc(50% + 4px)' }}
            transition={{ type: 'spring', stiffness: 550, damping: 32 }}
          />
          <button
            type="button"
            className={`${Classes.toggleBtn} ${billing === 'monthly' ? Classes.active : ''}`}
            onClick={() => setBilling('monthly')}
          >
            Monthly
          </button>
          <button
            type="button"
            className={`${Classes.toggleBtn} ${billing === 'yearly' ? Classes.active : ''}`}
            onClick={() => setBilling('yearly')}
          >
            Yearly
          </button>
        </div>
        <h5 className={Classes.toggleNote}>Save 15% by billing annually</h5>
      </motion.div>

      {/* Cards */}
      <motion.div className={Classes.plansGrid} variants={stagger}>
        {plans.map((p) => {
          const isCurrentPlan =
            hasActiveSubscription && currentPlanKey === p.key;

          return (
            <motion.div
              key={p.key}
              className={`${Classes.card}`}
              variants={cardIn}
              whileHover={{ y: -8, scale: 1.01 }}
              transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            >
              {/* Top */}
              <div className={`${Classes.cardTop} ${p.highlight ? Classes.cardPremium : ''}`}>
                <div className={Classes.cardHeader}>
                  <div className={Classes.cardTitle}>{p.title}</div>
                  <div className={Classes.cardSubtitle}>{p.subtitle}</div>

                  {/* Current plan badge */}
                  {isCurrentPlan && (
                    <div
                      style={{
                        marginTop: 6,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 10px',
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 600,
                        background: 'rgba(34,197,94,0.1)',
                        color: '#16a34a',
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: '#22c55e',
                        }}
                      />
                      Current plan
                    </div>
                  )}
                </div>

                <div className={Classes.priceRow}>{priceLabel(p)}</div>
                <div className={Classes.billedNote}>{billedNote()}</div>

                <button
                  type="button"
                  className={Classes.cta}
                  onClick={() => !isCurrentPlan && handleGetStarted(p)}
                  disabled={isCurrentPlan}
                  style={isCurrentPlan ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                >
                  {isCurrentPlan ? 'Current Plan' : p.cta}
                </button>
              </div>

              {/* Bottom */}
              <div className={Classes.cardBottom}>
                <div className={Classes.featuresTitle}>
                  {p.key === 'studio'
                    ? 'Everything in Individual Plus:'
                    : 'Included Features:'}
                </div>
                <ul className={Classes.features}>
                  {p.features.map((f, i) => (
                    <li key={i}>
                      <TickCircleIcon height="20" fill="var(--primary-color)" />
                      <h5>{f}</h5>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </motion.div>
  );
}
